// Quotes and their comparison (P2.7; 01-SPEC §3 step 3, §9 settings). Pure rules, unit-tested:
// totals in integer minor units, the quote-count rule (fewer than required needs a reason on any
// request, D50) and the price-variance flag against the last awarded price of the same item.

import { z } from 'zod';

export const QUOTE_CURRENCIES = ['SAR', 'AED', 'EGP', 'USD', 'EUR'] as const;
export type QuoteCurrency = (typeof QUOTE_CURRENCIES)[number];
/** Settings defaults (01-SPEC §9): quotes required 3, price variance 10%. */
export const DEFAULT_QUOTES_REQUIRED = 3;
export const DEFAULT_VARIANCE_PCT = 10;
const MAX_UNIT_PRICE = 100_000_000;

const text = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .regex(/^[^<>]*$/, 'Remove < and >');

export const quoteInputSchema = z.strictObject({
  supplierId: z.string().regex(/^SUP-[2-9A-HJ-NP-Z]{8}$/, 'Pick a supplier'),
  quoteRef: text(60).default(''),
  currency: z.enum(QUOTE_CURRENCIES),
  validUntil: z.iso.date('Pick a date'),
  leadTime: text(60).default(''),
  /** Unit price per request line, excluding VAT, in major units; index = line position. */
  unitPrices: z
    .array(z.number().nonnegative('Prices cannot be negative').max(MAX_UNIT_PRICE))
    .min(1)
    .max(50),
  /** Name of the quotation file already uploaded to the request folder, if any. */
  file: z.string().max(200).default(''),
});
export type QuoteInput = z.infer<typeof quoteInputSchema>;

export const selectQuoteSchema = z.strictObject({
  quoteId: z.string().regex(/^\d{1,12}$/),
  etag: z.string().min(1),
  reason: text(500).default(''),
});

export const noResponseSchema = z.strictObject({
  supplierId: z.string().regex(/^SUP-[2-9A-HJ-NP-Z]{8}$/),
});

/** Major units to integer minor units (all supported currencies use 2 decimals). */
export const minor = (major: number) => Math.round(major * 100);

/** Line total in minor units: quantity × unit price, rounded once. */
export const lineTotal = (qty: number, unitMinor: number) => Math.round(qty * unitMinor);

export function quoteTotal(lines: { qty: number }[], unitMinor: number[]): number {
  return lines.reduce((sum, l, i) => sum + lineTotal(l.qty, unitMinor[i] ?? 0), 0);
}

/** The key two descriptions of the same item share: lower case, letters and digits, one space. */
export function normaliseItem(description: string): string {
  return description
    .normalize('NFKC')
    .toLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, ' ')
    .trim()
    .slice(0, 120);
}

/** Percentage above (+) or below (−) the last price, one decimal; null without a last price. */
export function variancePct(unitMinor: number, lastMinor: number | null): number | null {
  if (lastMinor === null || lastMinor <= 0) return null;
  return Math.round(((unitMinor - lastMinor) / lastMinor) * 1000) / 10;
}

export const isFlagged = (pct: number | null, threshold: number) => pct !== null && pct > threshold;

export interface QuoteLineView {
  description: string;
  qty: number;
  unit: string;
  unitPrice: number;
  total: number;
  lastPrice: number | null;
  lastDate: string;
  variancePct: number | null;
  flagged: boolean;
}

export interface QuoteView {
  quoteId: string;
  etag: string;
  supplierId: string;
  supplierName: string;
  quoteRef: string;
  currency: string;
  /** Excluding VAT, minor units. */
  amount: number;
  validUntil: string;
  expired: boolean;
  leadTime: string;
  file: string;
  lines: QuoteLineView[];
  flagged: boolean;
  maxVariancePct: number | null;
  selected: boolean;
  selectedBy: string;
  exceptionReason: string;
  recordedBy: string;
  recordedAt: string;
}

export interface QuoteRuleState {
  /** Valid (not expired) quotes from different suppliers. */
  count: number;
  required: number;
  /** Selecting needs a written reason (fewer than required). */
  needsReason: boolean;
}

/** The quote-count rule (01-SPEC §3; D50). `today` is YYYY-MM-DD. */
export function quoteRule(
  quotes: { supplierId: string; validUntil: string }[],
  required: number,
  today: string,
): QuoteRuleState {
  const valid = new Set(
    quotes.filter((q) => q.validUntil.slice(0, 10) >= today).map((q) => q.supplierId),
  );
  return { count: valid.size, required, needsReason: valid.size < required };
}

/** Why a selection is refused, or null. */
export function selectionProblem(
  quote: { validUntil: string },
  rule: QuoteRuleState,
  reason: string,
  today: string,
): string | null {
  if (quote.validUntil.slice(0, 10) < today) return 'This quote has expired';
  if (rule.needsReason && reason.trim().length < 5) {
    return `Fewer than ${rule.required} valid quotes: write the reason`;
  }
  return null;
}

export interface QuotesPageBody {
  prNo: string;
  title: string;
  status: string;
  rush: boolean;
  rushReason: string;
  currency: string;
  lines: { description: string; specs: string; qty: number; unit: string }[];
  quotes: QuoteView[];
  /** Suppliers asked (RFQs) with their latest status, then other approved suppliers. */
  suppliers: { supplierId: string; name: string; rfqStatus: string }[];
  rule: QuoteRuleState;
  variancePct: number;
  canEdit: boolean;
}
